/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { BAProfile, BAProvidersSync } from '../api/user/user.models';

type Provider = {
  provider: string;
  status: BAProvidersSync['status'];
  userName?: string;
};

export type AccountProviders = {
  accountProviders: Provider[];
  accountStatus?: BAProvidersSync['status'] | 'no-game-accounts' | 'no-game-ownership';
  defaultProvider?: string;
};

export const getAccountProviders = (
  accounts: BAProfile['accounts'],
  providers: BAProvidersSync[],
  gameLibrary: BAProfile['gameLibrary'] | null,
): AccountProviders => {
  const hasAccounts = accounts.length > 0;
  const libraryProviders = gameLibrary?.dbd?.providerData || [];
  const hasOwnership = providers.length > 0 || libraryProviders.length > 0;

  const defaultProvider = getDefaultProvider(providers, libraryProviders);
  const accountStatus = getAccountStatus(hasAccounts, hasOwnership, providers, defaultProvider);

  const accountProviders: Provider[] = providers.map((provider) => {
    const userName = accounts.find((x) => x.type === provider.provider)?.userName;
    return {
      provider: provider.provider,
      status: provider.status,
      userName: userName,
    };
  });

  return {
    accountProviders,
    accountStatus,
    defaultProvider,
  };
};

const getDefaultProvider = (
  providers: BAProvidersSync[],
  libraryProviders: BAProfile['gameLibrary']['dbd']['providerData'],
): string | undefined => {
  if (providers.length > 0) {
    if (providers.find((provider) => provider.status === 'synced')) {
      // If any provider is synced, return 'bhvr' as the default provider
      return 'bhvr';
    } else {
      // If no provider is synced, return 'steam' if available, otherwise return the first provider
      return providers.find((provider) => provider.provider === 'steam')?.provider || providers[0]?.provider;
    }
  } else {
    return (
      libraryProviders.find((provider) => provider.providerName === 'steam')?.providerName ||
      libraryProviders[0]?.providerName
    );
  }
};

const getAccountStatus = (
  hasAccounts: boolean,
  hasOwnership: boolean,
  providers: BAProvidersSync[],
  defaultProvider: string | undefined,
): AccountProviders['accountStatus'] => {
  if (!hasAccounts) return 'no-game-accounts';
  if (!hasOwnership) return 'no-game-ownership';

  if (defaultProvider === 'bhvr') {
    return 'synced';
  }

  return providers.find((provider) => provider.provider === defaultProvider)?.status || 'unknown';
};
